/**
 * <header-behavior> — what the header needs a script for.
 *
 * Layout: below 990px the header is always compact (CSS only). Above, the desktop menu is shown first; if its
 * items wrap onto a second line or overflow (long menu, long labels in another language, wide logo), the header
 * gets data-compact and the burger menu takes over. It is measured again whenever the header changes width and once
 * the fonts are loaded, since both change the width of the labels. Without this script the menu simply wraps.
 * The height of a sticky header is published as --nt-header-h, used for scroll-padding-top.
 *
 * Burger menu (a native <details>): Escape closes it and gives the focus back to its button; a press outside it,
 * or the focus moving out of it, closes it too.
 */

const MARGIN = 4;

class HeaderBehavior extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('.header');
    if (!this.header) return;

    this.width = 0;
    this.frame = 0;
    this.observer = new ResizeObserver(() => {
      this.publishHeight();
      if (this.header.clientWidth !== this.width) this.schedule();
    });
    this.observer.observe(this.header);
    document.fonts?.ready.then(this.schedule);
    this.publishHeight();
    this.schedule();

    this.burger = this.header.querySelector('.header__mobile');
    if (this.burger) {
      this.burger.addEventListener('keydown', this.onKeydown);
      this.burger.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onPointerDown);
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.burger?.removeEventListener('keydown', this.onKeydown);
    this.burger?.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('pointerdown', this.onPointerDown);
  }

  /** The sticky header hides what is under it: its height tells the page how much room to keep when scrolling to focus. */
  publishHeight() {
    const root = document.documentElement;
    if (this.header.classList.contains('header--sticky'))
      root.style.setProperty('--nt-header-h', `${this.header.offsetHeight}px`);
    else root.style.removeProperty('--nt-header-h');
  }

  schedule = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.measure);
  };

  measure = () => {
    const { header } = this;
    this.width = header.clientWidth;

    // Lay the header out in its desktop state, measure, then settle on the state to keep. Nothing is painted in between.
    header.removeAttribute('data-compact');
    const nav = header.querySelector('.header__nav');
    const items = [...header.querySelectorAll('.header__nav > ul > li')];
    if (!nav || !items.length) return;

    const wraps = items.some((item) => item.offsetTop > items[0].offsetTop + MARGIN);
    const overflows = nav.scrollWidth > nav.clientWidth + 1;
    header.toggleAttribute('data-compact', wraps || overflows);
  };

  close(returnFocus = false) {
    if (!this.burger?.open) return;
    this.burger.open = false;
    if (returnFocus) this.burger.querySelector('summary').focus();
  }

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.burger.open) {
      event.preventDefault();
      this.close(true);
    }
  };

  onFocusOut = (event) => {
    if (event.relatedTarget && !this.burger.contains(event.relatedTarget)) this.close();
  };

  onPointerDown = (event) => {
    if (!this.burger.contains(event.target)) this.close();
  };
}

if (!customElements.get('header-behavior')) customElements.define('header-behavior', HeaderBehavior);
